import React, { useEffect, useState } from 'react';
import { TextField, Button, Grid2, Alert, FormControl, Autocomplete, Checkbox, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportServices } from '../../services/reportServices';
import { jobServices } from '../../services/jobServices';
import { userServices } from '../../services/userServices';
import { useAuth } from '../../services/authentication/authContext';
import Clear from '@mui/icons-material/Clear';
import GetApp from '@mui/icons-material/GetApp';
import { downloadReport } from '../../utils/downloadReport ';
import '../../css/Reports.css';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import ReportGenerateButton from '../common/ReportGenerateButton';
import PageHeader from '../ui/PageHeader';

const JobCompletionReport = ({ showHeader = true }) => {

    const [clientList, setClientList] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [userList, setUserList] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const { accessToken } = useAuth();
    const [clientsLoading, setClientsLoading] = useState(false);

    useEffect(() => {
        if (accessToken) {
            setLoading(true);
            userServices.getUsersByRoleName()
                .then((users) => {
                    setUserList(users);
                    setLoading(false);
                })
                .catch((error) => {
                    setError("Failed to load users data.");
                    setLoading(false);
                });
        }
    }, [accessToken]);

    useEffect(() => {
        if (accessToken && selectedUserIds.length > 0) {
            setClientsLoading(true);
            jobServices.getClientsByUserIds(selectedUserIds.join(','))
                .then((clients) => {
                    setClientList(clients || []);
                    setClientsLoading(false);
                    // Filter out selected clients that are no longer in the new client list
                    const validClientIds = clients.map(c => c.id);
                    setSelectedClientIds(prev => prev.filter(id => validClientIds.includes(id)));
                })
                .catch((error) => {
                    if (error.response && error.response.status === 404) {
                        setClientList([]);
                        setSelectedClientIds([]);
                    } else {
                        setError("Failed to load clients data.");
                    }
                    setClientsLoading(false);
                });
        } else {
            setClientList([]);
            setSelectedClientIds([]);
        }
    }, [selectedUserIds, accessToken]);

    const handleClear = () => {
        setStartDate('');
        setEndDate('');
        setSelectedClientIds([]);
        setSelectedUserIds([]);
    };

    const handleReport = () => {
        setGenerating(true);
        const formData = {
            startDate,
            endDate,
            clientIds: selectedClientIds,
            userIds: selectedUserIds
        };
        if (startDate <= endDate) {
            reportServices.downloadCompletedJobsReport(formData)
                .then((response) => {
                    downloadReport(response);
                    setGenerating(false);
                    setSuccessMessage("Report Generated Successfully!");
                    setTimeout(() => setSuccessMessage(null), 4000);

                    setSelectedClientIds([]);
                    setSelectedUserIds([]);
                    setEndDate('');
                    setStartDate('');
                })
                .catch((error) => {
                    setError(error.message);
                    setTimeout(() => {
                        setError(null);
                    }, 5000);
                    setGenerating(false);
                });
        } else {
            setError('Error! Start date must be earlier than end date.');
            setTimeout(() => {
                setError(null);
            }, 5000);
            setGenerating(false);
        }
    };

    return (
        <div className={showHeader ? "reports-container" : ""}>
            {showHeader && (
                <PageHeader title="Job Completion Report" />
            )}
            <Box className={showHeader ? "report-box" : ""}>

                {successMessage && (
                    <Alert variant="filled" severity="success" style={{ marginBottom: '1.25rem' }}>
                        {successMessage}
                    </Alert>
                )}

                {error && (
                    <Alert variant="outlined" severity="error" style={{ marginBottom: '1.25rem' }}>
                        {error}
                    </Alert>
                )}

                <form style={{ marginTop: '1.25rem' }}>
                    <Grid2 container spacing={3}>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <TextField
                                type="date"
                                label={t('report.startDate')}
                                variant="outlined"
                                fullWidth
                                required
                                value={startDate || ''}
                                onChange={(e) => setStartDate(e.target.value)}
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <TextField
                                type="date"
                                label={t('report.endDate')}
                                variant="outlined"
                                fullWidth
                                required
                                value={endDate || ''}
                                disabled={startDate ? false : true}
                                onChange={(e) => setEndDate(e.target.value)}
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth margin="normal">
                                <Autocomplete
                                    multiple
                                    id="multi-user-checkbox"
                                    options={[{ id: 'all', firstName: 'Select All' }, ...userList]}
                                    disableCloseOnSelect
                                    value={userList.filter(user => selectedUserIds.includes(user.id))}
                                    getOptionLabel={(user) =>
                                        user.id === 'all'
                                            ? 'Select All'
                                            : `${user.firstName} ${user.middleName ?? ''} ${user.lastName}`
                                    }
                                    onChange={(event, newValue) => {
                                        const isSelectAllClicked = newValue.some(user => user.id === 'all');

                                        if (isSelectAllClicked) {
                                            const allUserIds = userList.map(user => user.id);
                                            const isAllSelected = selectedUserIds.length === userList.length;

                                            if (isAllSelected) {
                                                setSelectedUserIds([]);
                                            } else {
                                                setSelectedUserIds(allUserIds);
                                            }
                                        } else {
                                            const filtered = newValue.filter(user => user.id !== 'all');
                                            setSelectedUserIds(filtered.map((user) => user.id));
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option, { selected }) => {
                                        const isAllSelected = selectedUserIds.length === userList.length;

                                        return (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    style={{ marginRight: 8 }}
                                                    checked={option.id === 'all' ? isAllSelected : selected}
                                                />
                                                {option.id === 'all'
                                                    ? 'Select All'
                                                    : `${option.firstName} ${option.middleName ?? ''} ${option.lastName}`}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="User"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth margin="normal">
                                <Autocomplete
                                    multiple
                                    id="multi-client-checkbox"
                                    options={[{ id: 'all', name: 'Select All' }, ...clientList]}
                                    disableCloseOnSelect
                                    loading={clientsLoading}
                                    disabled={selectedUserIds.length === 0}
                                    value={clientList.filter(client => selectedClientIds.includes(client.id))}
                                    getOptionLabel={(client) =>
                                        client.id === 'all'
                                            ? 'Select All'
                                            : `${client.name}`
                                    }
                                    onChange={(event, newValue) => {
                                        const isSelectAllClicked = newValue.some(client => client.id === 'all');

                                        if (isSelectAllClicked) {
                                            const allClientIds = clientList.map(client => client.id);
                                            const isAllSelected = selectedClientIds.length === clientList.length;

                                            if (isAllSelected) {
                                                setSelectedClientIds([]);
                                            } else {
                                                setSelectedClientIds(allClientIds);
                                            }
                                        } else {
                                            const filtered = newValue.filter(client => client.id !== 'all');
                                            setSelectedClientIds(filtered.map((client) => client.id));
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option, { selected }) => {
                                        const isAllSelected = selectedClientIds.length === clientList.length;

                                        return (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                    checkedIcon={<CheckBox fontSize="small" />}
                                                    style={{ marginRight: 8 }}
                                                    checked={option.id === 'all' ? isAllSelected : selected}
                                                />
                                                {option.id === 'all'
                                                    ? 'Select All'
                                                    : `${option.name}`}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Client"
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }} className="gridContainer">
                            <ReportGenerateButton
                                onClick={handleReport}
                                loading={generating}
                                disabled={!(startDate && endDate && selectedClientIds.length > 0 && selectedUserIds.length > 0) || (Boolean(loading) || generating)}
                                className={`commonButton ${(startDate && endDate && selectedClientIds.length > 0 && selectedUserIds.length > 0) ? 'reportButtonSuccess' : 'reportButtonDefault'}`}
                                icon={<GetApp className="buttonIcon" />}
                                label={t('job.report')}
                                loadingLabel="Preparing your report..."
                            />
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleClear}
                                className={`commonButton clearButton`}
                                disabled={(Boolean(loading) || generating)}
                            >
                                <Clear className="buttonIcon" /> {t('job.clear')}
                            </Button>
                        </Grid2>

                    </Grid2>
                </form>
            </Box>
        </div>
    );
};

export default JobCompletionReport;
